import type { RecallProposalsPublishApiData, RecallSeason } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { filterUnitFor, filterUrlState, useFilterSpecValue, type FilterControl } from 'noddi-ui-web';
import { isHalfYearName, RecallSeasonMode } from 'noddi-util';

type RecallSeasonQuery = Pick<NonNullable<RecallProposalsPublishApiData['query']>, 'seasons' | 'excludedSeasons'>;
export type RecallSeasonName = NonNullable<RecallSeasonQuery['seasons']>[number];

const recallSeasonUnit = filterUnitFor<RecallSeasonQuery>();

/** A season name read from the URL; anything that is no half-year is dropped rather than sent. */
const parseSeason = (value: string) => (isHalfYearName<RecallSeasonName>(value) ? value : undefined);
const seasonNames = (seasons: Pick<RecallSeason, 'name'>[]): RecallSeasonName[] => seasons.map((season) => season.name);

const SEASONS_KEY = filterUrlState.urlKey('seasons');
const EXCLUDED_SEASONS_KEY = filterUrlState.urlKey('excludedSeasons');

const sameValues = <T>(a: readonly T[], b: readonly T[]) =>
  a.length === b.length && a.every((value) => b.includes(value));

const SEASON_DESCRIPTION =
  'A recall season is a period of time when recall campaigns are run. Seasons are grouped by semiannual periods. H1 is from January 1st to June 30th, and H2 is from July 1st to December 31st.';

/** The seasons both queries load: the current one seeds the default and the list scope, so they cannot wait. */
const useRecallSeasonData = () => {
  // eslint-disable-next-line no-restricted-properties -- seeds the default selection and the list scope, so it cannot wait for the sidebar
  const current = noddiAsync.useGet({ type: URLKeys.getRecallSeasonCurrent });
  // eslint-disable-next-line no-restricted-properties -- resolves the season names the list query sends
  const all = noddiAsync.useGet({ type: URLKeys.getRecallSeasons });
  return { current, all, currentSeason: current.data };
};

type RecallSeasonData = ReturnType<typeof useRecallSeasonData>;

/**
 * The page's season scope: what the season filter of the All tab sends (`seasons` + `excludedSeasons`; the current
 * season until the user picks, nothing when cleared). One scope for every reader -- the current-season tabs' lists,
 * the work tab counts and Send reminders -- so a tab's count and its list never describe different seasons. Never
 * read `seasons` from the URL by hand: it holds a cleared filter as `''` and excludes elsewhere.
 */
export const useRecallSeasonQuery = () => {
  const { data, ...scope } = useRecallSeasons();
  return scope;
};

const useRecallSeasons = () => {
  const data = useRecallSeasonData();
  const query = useFilterSpecValue(seasonFilterSpec(RecallSeasonMode.ALL, data));
  const errors = [data.current.error, data.all.error].filter(
    (error): error is NonNullable<typeof error> => error != null
  );
  return { data, query, isSeasonsResolved: !!data.currentSeason, currentRecallSeason: data.currentSeason, errors };
};

/**
 * The season scope of a recall list. Current: no control, the page's season scope (`useRecallSeasonQuery`). All:
 * every season, the current one until the user picks. Previous: seasons before the current one, all of them until
 * the user picks.
 */
export const useRecallSeasonScope = ({ seasonMode }: { seasonMode: RecallSeasonMode }) => {
  const { data, query, isSeasonsResolved, currentRecallSeason, errors } = useRecallSeasons();
  const isPrevious = seasonMode === RecallSeasonMode.PREVIOUS;

  const season =
    seasonMode === RecallSeasonMode.CURRENT ? recallSeasonUnit.locked(query) : seasonFilterSpec(seasonMode, data);

  return {
    season,
    isSeasonsResolved: isPrevious ? !!data.all.data && isSeasonsResolved : isSeasonsResolved,
    currentRecallSeason,
    errors
  };
};

/** The season control of the All or Previous tab, on the shared `seasons` URL state. */
const seasonFilterSpec = (seasonMode: RecallSeasonMode, { current, all, currentSeason }: RecallSeasonData) => {
  const isPrevious = seasonMode === RecallSeasonMode.PREVIOUS;
  const offered = isPrevious
    ? currentSeason
      ? (all.data ?? []).filter((season) => season.dateFrom < currentSeason.dateFrom)
      : []
    : (all.data ?? []);
  const offeredNames = seasonNames(offered);
  const defaults: RecallSeasonName[] =
    seasonMode === RecallSeasonMode.ALL && currentSeason ? seasonNames([currentSeason]) : [];

  return recallSeasonUnit.compose(
    `seasons:${seasonMode}`,
    {
      [SEASONS_KEY]: filterUrlState.selection(parseSeason),
      [EXCLUDED_SEASONS_KEY]: filterUrlState.excluded(parseSeason)
    },
    (values, setValues) => {
      const stored = values[SEASONS_KEY] as RecallSeasonName[] | null | undefined;
      const excluded = (values[EXCLUDED_SEASONS_KEY] as RecallSeasonName[] | undefined) ?? [];
      const included = filterUrlState.includedValues({ include: stored, exclude: excluded }, defaults);
      const write = (include: RecallSeasonName[] | null | undefined, exclude: RecallSeasonName[]) =>
        setValues(() => ({ [SEASONS_KEY]: include, [EXCLUDED_SEASONS_KEY]: exclude }));
      const parseAll = (picked: (string | number)[]) => picked.flatMap((value) => parseSeason(String(value)) ?? []);

      const control: FilterControl = {
        id: 'seasons',
        type: 'multi-select',
        title: 'Recall Seasons',
        description: SEASON_DESCRIPTION,
        options: offeredNames.map((name) => ({ value: name, label: name })),
        load: {
          isLoading: all.isLoading || current.isLoading,
          isError: all.isError || current.isError,
          isFetching: all.isFetching || current.isFetching,
          onRetry: () => {
            if (all.isError) {
              void all.refetch();
            }
            if (current.isError) {
              void current.refetch();
            }
          }
        },
        canExclude: true,
        value: { include: included, exclude: excluded },
        onChange: (next) => {
          const nextExclude = parseAll(next.exclude);
          const nextInclude = parseAll(next.include).filter((value) => !nextExclude.includes(value));
          // A selection the user did not touch keeps following the current season.
          const untouched = stored === undefined && sameValues(nextInclude, included);
          write(
            untouched ? undefined : filterUrlState.toStored(nonEmpty(nextInclude), defaults.length > 0),
            nextExclude
          );
        },
        reset: () => write(undefined, []),
        isInitial: stored === undefined && excluded.length === 0
      };

      // Previous seasons only: a picked season outside them is dropped, and none picked means all of them.
      const picked = isPrevious ? included.filter((name) => offeredNames.includes(name)) : included;
      const seasons = isPrevious && picked.length === 0 ? offeredNames : picked;
      return { controls: control, params: { seasons, excludedSeasons: excluded } };
    }
  );
};

const nonEmpty = <T>(values: T[]) => (values.length > 0 ? values : undefined);
